import { useStoryblokApi } from "@storyblok/react";
import { Suspense, use, useMemo } from "react";
import { getEnvironmentVersion } from "../../utils/getEnvironmentVersion";
import { ArticleList, fetchCategoryNames } from "./articleCards";

const RELATED_COUNT = 3;

// Shared categories are the main signal; shared tags add extra weight, so
// among posts in the same category the ones with overlapping tags rank first.
const CATEGORY_WEIGHT = 3;
const TAG_WEIGHT = 1;

// Tag slugs used for internal bookkeeping on the old site; sharing one says
// nothing about whether two posts are related.
const IGNORED_TAGS = new Set(["seo-content", "community-content"]);

const sharedCount = (a = [], b = [], ignored = new Set()) => {
  const other = new Set(b);
  return new Set(a.filter((slug) => other.has(slug) && !ignored.has(slug))).size;
};

const relatedScore = (post, candidate) =>
  CATEGORY_WEIGHT * sharedCount(post.categories, candidate.categories) +
  TAG_WEIGHT * sharedCount(post.tags, candidate.tags, IGNORED_TAGS);

// Storyblok's multilink field is a single link object at rest, not an array
// — an unused one (the common case here, since the import script never set
// editor picks) comes back as either `[]` or a lone empty `{fieldtype:
// "multilink", ...}` object depending on whether it's ever been saved
// through the visual editor. Normalize both to an array before filtering.
const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

// Editor picks (the related_posts field, resolved to stories by App) come
// first; the rest are filled by score, newest first on ties.
const pickRelated = (post, candidates) => {
  const manual = asArray(post.related_posts).filter(
    (story) => typeof story === "object" && story.uuid
  );
  const manualIds = new Set(manual.map((story) => story.uuid));

  const scored = candidates
    .filter((story) => story.content._uid !== post._uid && !manualIds.has(story.uuid))
    .map((story, recency) => ({ story, recency, score: relatedScore(post, story.content) }))
    .sort((a, b) => b.score - a.score || a.recency - b.recency)
    .map(({ story }) => story);

  return [...manual, ...scored].slice(0, RELATED_COUNT);
};

export const RelatedPosts = ({ post }) => {
  const storyblokApi = useStoryblokApi();

  const candidatesPromise = useMemo(
    () =>
      storyblokApi.getAll("cdn/stories", {
        version: getEnvironmentVersion(),
        content_type: "blogpost",
        sort_by: "content.published_at:desc",
        excluding_fields: "maincontent",
      }),
    [storyblokApi],
  );
  const categoryNamesPromise = useMemo(() => fetchCategoryNames(storyblokApi), [storyblokApi]);

  return (
    <section className="related-posts">
      <h2 className="related-posts-heading">Related posts</h2>
      {/* Keyed by post so moving between posts mounts a fresh boundary (see
          the matching note in BlogOverview). */}
      <Suspense key={post._uid} fallback={null}>
        <RelatedList post={post} getCandidates={candidatesPromise} getCategoryNames={categoryNamesPromise} />
      </Suspense>
    </section>
  );
};

const RelatedList = ({ post, getCandidates, getCategoryNames }) => {
  const candidates = use(getCandidates);
  const categoryNames = use(getCategoryNames);
  const related = pickRelated(post, candidates);

  return (
    <ul className="blog-card-grid">
      <ArticleList articles={related} categoryNames={categoryNames} />
    </ul>
  );
};
